import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LANDING } from '@/constants/testIds';
import ThemeToggle from '@/components/ThemeToggle';
import api, { apiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import {
    ArrowRight, ShieldCheck, Brain, FileCheck2, Camera, Scale, Layers,
    Building2, Briefcase, Sparkles, Globe, BadgeCheck, BellRing,
    Mail, Phone, MapPin, Send, ChevronDown, HelpCircle, Smartphone, X as XIcon,
} from 'lucide-react';

const LOGO_FULL = "https://customer-assets.emergentagent.com/job_audit-ia-1/artifacts/bwe3ss1d_1.png";
const LOGO_WORDMARK = "https://customer-assets.emergentagent.com/job_audit-ia-1/artifacts/6tkmr7v8_3.png";

const C = {
    orange: '#E08B3C',
    teal: '#1F8FB8',
    green: '#1E9E47',
    ink: '#0A0A0A',
};

function HexIcon({ icon: Icon, accent = C.teal, size = 56 }) {
    return (
        <div
            className="relative flex items-center justify-center shrink-0"
            style={{
                width: size, height: size,
                clipPath: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)',
                background: `linear-gradient(135deg, ${accent} 0%, ${C.orange} 100%)`,
            }}
        >
            <div
                className="flex items-center justify-center"
                style={{
                    width: size - 6, height: size - 6,
                    clipPath: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)',
                    background: 'white',
                }}
            >
                <Icon style={{ color: accent, width: size * 0.4, height: size * 0.4 }} />
            </div>
        </div>
    );
}

const CLIENT_FEATURES = [
    { icon: Camera, title: 'Análisis de evidencias por visión IA', desc: 'Cada fotografía del auditor es analizada para detectar respaldo o contradicciones con la respuesta.', accent: C.orange },
    { icon: Scale, title: 'Marco jurídico trazable', desc: 'Cada hallazgo queda respaldado por la cláusula o artículo específico de la norma evaluada.', accent: C.green },
    { icon: FileCheck2, title: 'Puntaje ponderado automático', desc: 'Pesos calibrados que suman 100% y dictamen sugerido según el umbral configurable.', accent: C.teal },
    { icon: ShieldCheck, title: 'Roles separados, decisión final del admin', desc: 'Clientes solicitan, auditores aplican, sólo el administrador aprueba o rechaza.', accent: C.orange },
];

const PROVIDER_FEATURES = [
    { icon: BadgeCheck, title: 'Distintivo verificable', desc: 'Cada distintivo emitido es único, trazable y consultable públicamente.', accent: C.green },
    { icon: Globe, title: 'Catálogo público de proveedores', desc: 'Aparece en el directorio AIGIS y atrae clientes que buscan compliance auditado.', accent: C.teal },
    { icon: BellRing, title: 'Alertas de vencimiento', desc: 'Renueva a tiempo. Notificaciones automáticas antes de que tu distintivo expire.', accent: C.orange },
];

export default function Landing() {
    const navigate = useNavigate();
    const [showInspectorBanner, setShowInspectorBanner] = useState(false);

    useEffect(() => {
        // Detectar tablet/móvil y ofrecer la app inspector
        const dismissed = localStorage.getItem('inspector_banner_dismissed');
        if (dismissed === '1') return;
        const ua = navigator.userAgent.toLowerCase();
        const isMobile = /iphone|ipad|ipod|android/i.test(ua) ||
            (navigator.maxTouchPoints > 1 && window.innerWidth < 1200);
        if (isMobile) setShowInspectorBanner(false);
    }, []);

    const dismissInspectorBanner = () => {
        localStorage.setItem('inspector_banner_dismissed', '1');
        setShowInspectorBanner(false);
    };

    return (
        <div className="bg-white text-[#0A0A0A] relative overflow-hidden">
            {/* Barra de auditor retirada: el acceso de auditor no se anuncia
                 en la pagina publica. Solo el administrador da de alta auditores
                 y les indica como instalar la app. */}

            {/* Subtle hexagon watermark */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-[0.025]"
                style={{
                    backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='104' viewBox='0 0 120 104'><polygon points='60,2 118,32 118,72 60,102 2,72 2,32' fill='none' stroke='%230A0A0A' stroke-width='1.5'/></svg>")`,
                    backgroundSize: '160px 138px',
                }}
            />

                <div aria-hidden className="absolute inset-0 bg-grid hidden dark:block opacity-[0.03] pointer-events-none"
                    style={{
                        backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='104' viewBox='0 0 120 104'><polygon points='60,2 118,32 118,72 60,102 2,72 2,32' fill='none' stroke='%23FAFAFA' stroke-width='1.5'/></svg>")`,
                        backgroundSize: '160px 138px',
                    }}
                />
            {/* Nav */}
            <header className="sticky top-0 z-50 bg-white/95 dark:bg-[#0A0A0A]/95 backdrop-blur border-b border-gray-200 dark:border-gray-800">
                <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center">
                        <img src={LOGO_WORDMARK} alt="AIGIS CERTIFICATE · Safety Solutions" className="h-16 w-auto object-contain" />
                    </Link>
                    <nav className="flex items-center gap-2">
                        <a href="#clientes" className="hidden md:inline text-sm font-medium px-3 py-2 hover:text-[#1F8FB8] transition-colors">Clientes principales</a>
                        <a href="#proveedores" className="hidden md:inline text-sm font-medium px-3 py-2 hover:text-[#1F8FB8] transition-colors">Proveedores</a>
                        <a href="#nosotros" className="hidden md:inline text-sm font-medium px-3 py-2 hover:text-[#1F8FB8] transition-colors">Nosotros</a>
                        <ThemeToggle />
                        <Link
                            to="/login"
                            data-testid={LANDING.loginNav}
                            className="text-sm font-medium px-4 py-2 border border-transparent hover:border-gray-300 dark:hover:border-gray-700 transition-colors"
                        >
                            Iniciar sesión
                        </Link>
                        <a
                            href="#contacto"
                            data-testid={LANDING.registerNav}
                            className="text-sm font-bold px-4 py-2 text-white transition-colors"
                            style={{ background: C.orange }}
                            onMouseOver={(e) => (e.currentTarget.style.background = '#c47728')}
                            onMouseOut={(e) => (e.currentTarget.style.background = C.orange)}
                            title="El alta la realiza el administrador. Contáctanos para dar de alta tu cuenta."
                        >
                            Solicitar alta
                        </a>
                    </nav>
                </div>
            </header>

            {/* Hero */}
            <section className="relative border-b border-gray-200">
                <div className="max-w-[1400px] mx-auto px-6 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7 relative z-10">
                        <div className="inline-flex items-center gap-2 mb-6 border-2 px-3 py-1.5" style={{ borderColor: C.green, background: 'rgba(30,158,71,0.06)' }}>
                            <span className="inline-block w-2 h-2" style={{ background: C.green }}></span>
                            <span className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: C.green }}>
                                Ecosistema global de distintivos de cumplimiento
                            </span>
                        </div>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-[64px] font-black leading-[1.05] tracking-tight mb-8">
                            <span style={{ color: C.teal }}>Conéctate al ecosistema líder</span>{' '}
                            y{' '}
                            <span style={{ color: C.orange }}>toma el control</span>{' '}
                            de tus{' '}
                            <span style={{ color: C.green }}>distintivos globales</span>.
                        </h1>
                        <p className="text-lg text-gray-700 max-w-2xl mb-8 leading-relaxed">
                            <span className="font-semibold">No solo es un sello. Es tu ventaja competitiva.</span>{' '}
                            Porque <span className="font-bold" style={{ color: C.green }}>AIGIS CERTIFICATE</span> te acompaña a
                            demostrar que lo haces mejor que los demás.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <a
                                href="#contacto"
                                data-testid={LANDING.heroCTA}
                                className="text-white px-7 py-4 font-bold inline-flex items-center gap-2 transition-colors"
                                style={{ background: C.orange }}
                                onMouseOver={(e) => (e.currentTarget.style.background = '#c47728')}
                                onMouseOut={(e) => (e.currentTarget.style.background = C.orange)}
                            >
                                Solicitar alta <ArrowRight className="w-4 h-4" />
                            </a>
                            <Link
                                to="/login"
                                className="border-2 border-[#0A0A0A] hover:bg-gray-50 text-[#0A0A0A] px-7 py-4 font-bold transition-colors"
                            >
                                Iniciar sesión
                            </Link>
                        </div>
                    </div>

                    <div className="lg:col-span-5 relative">
                        <div className="border border-gray-200 bg-black p-3 relative">
                            <img
                                src={LOGO_FULL}
                                alt="AIGIS Certificate — Safety Solutions"
                                className="w-full h-[480px] object-contain bg-black p-6"
                            />
                            <div className="absolute top-5 left-5 bg-white border-2 px-3 py-2" style={{ borderColor: C.orange }}>
                                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold" style={{ color: C.orange }}>
                                    ◆ IA aplicada en tiempo real
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Resultados / Stats section */}
            <section className="border-b border-gray-200 dark:border-gray-800 relative overflow-hidden" style={{ background: '#0A0A0A' }}>
                <div
                    aria-hidden
                    className="absolute inset-0 opacity-[0.04] pointer-events-none"
                    style={{
                        backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='104' viewBox='0 0 120 104'><polygon points='60,2 118,32 118,72 60,102 2,72 2,32' fill='none' stroke='%23FAFAFA' stroke-width='1.5'/></svg>")`,
                        backgroundSize: '160px 138px',
                    }}
                />
                <div className="max-w-[1400px] mx-auto px-6 py-20 relative z-10">
                    <div className="text-center max-w-3xl mx-auto mb-14">
                        <div className="text-sm uppercase tracking-[0.25em] font-bold mb-4" style={{ color: C.orange }}>
                            ◆ Nuestros resultados
                        </div>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.1] mb-5 text-white">
                            Resultados que hablan por <span style={{ color: C.green }}>sí solos</span>
                        </h2>
                        <p className="text-base lg:text-lg text-white/70 leading-relaxed">
                            Plataforma probada en el mercado global de distintivos de cumplimiento.
                        </p>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
                        {[
                            { value: '500+', label: 'Auditorías realizadas', color: C.teal },
                            { value: '98%', label: 'Tasa de cumplimiento', color: C.green },
                            { value: '40+', label: 'Normas NOM & ISO cubiertas', color: C.orange },
                            { value: '3x', label: 'Más rápido que papel', color: '#9D8DF1' },
                        ].map((s) => (
                            <div key={s.label} className="text-center px-4 py-2">
                                <div
                                    translate="no"
                                    className="font-display font-black tracking-tight mb-3 leading-none whitespace-nowrap"
                                    style={{ fontSize: 'clamp(40px, 5vw, 72px)', color: s.color }}
                                >
                                    {s.value}
                                </div>
                                <div className="text-sm uppercase tracking-[0.15em] font-medium text-white/80">
                                    {s.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Profile selector */}
            <section className="border-b border-gray-200 dark:border-gray-800 relative bg-[#FAFAFA] dark:bg-[#0F0F0F]">
                <div className="max-w-[1400px] mx-auto px-6 py-24 relative z-10">
                    <div className="text-center max-w-3xl mx-auto mb-14">
                        <div className="text-sm uppercase tracking-[0.25em] font-bold mb-5" style={{ color: C.teal }}>
                            ◆ Elige tu perfil
                        </div>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.1] mb-6">
                            ¿Cómo deseas empezar con <span style={{ color: C.green }}>AIGIS</span>?
                        </h2>
                        <p className="text-base lg:text-xl text-gray-700 dark:text-gray-300 leading-relaxed max-w-2xl mx-auto">
                            Selecciona tu perfil para acceder a las herramientas personalizadas diseñadas para tu rol
                            en el ecosistema de distintivos global.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <ProfileCard
                            icon={Building2}
                            accent={C.teal}
                            badge="Soy Cliente principal"
                            title="Busco obtener mi distintivo de cumplimiento"
                            body="Gestiona tus auditorías, obtén tu distintivo de cumplimiento conforme a normas NOM e ISO y mantén tu negocio en cumplimiento total."
                            anchor="clientes"
                            roleParam="client"
                        />
                        <ProfileCard
                            icon={Briefcase}
                            accent={C.orange}
                            badge="Soy Proveedor"
                            title="Busco avalar mis servicios"
                            body="Demuestra tu cumplimiento a tus clientes, aparece en el catálogo público y fortalece tu marca."
                            anchor="proveedores"
                            roleParam="provider"
                        />
                    </div>
                </div>
            </section>

            {/* Clientes principales */}
            <section id="clientes" className="border-b border-gray-200">
                <div className="max-w-[1400px] mx-auto px-6 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-5 lg:sticky lg:top-28">
                        <div className="label-xs mb-3" style={{ color: C.teal }}>Para Clientes principales</div>
                        <h2 className="font-display text-3xl lg:text-5xl font-black tracking-tight leading-tight mb-6">
                            <span style={{ color: C.teal }}>Lidera</span> con cumplimiento normativo{' '}
                            <span style={{ color: C.green }}>global</span>.
                        </h2>
                        <p className="text-lg text-gray-700 leading-relaxed mb-6">
                            Eleva los estándares y garantiza el cumplimiento de tu establecimiento con un motor de IA
                            que automatiza la auditoría y reduce semanas de proceso a minutos.
                        </p>
                        <a
                            href="#contacto"
                            className="inline-flex items-center gap-2 text-white px-6 py-3 font-bold transition-colors"
                            style={{ background: C.orange }}
                            onMouseOver={(e) => (e.currentTarget.style.background = '#c47728')}
                            onMouseOut={(e) => (e.currentTarget.style.background = C.orange)}
                        >
                            Solicitar alta como cliente <ArrowRight className="w-4 h-4" />
                        </a>
                    </div>
                    <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="client-features-grid">
                        {CLIENT_FEATURES.map((f) => (
                            <div key={f.title} className="border border-gray-200 dark:border-gray-800 bg-white p-7 hover:border-[#0A0A0A] dark:hover:border-white transition-colors group">
                                <HexIcon icon={f.icon} accent={f.accent} size={56} />
                                <div className="font-display text-lg font-bold mt-5 mb-2.5">{f.title}</div>
                                <div className="text-sm text-gray-600 leading-relaxed">{f.desc}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Proveedores */}
            <section id="proveedores" className="border-b border-gray-200 dark:border-gray-800 bg-[#FAFAFA] dark:bg-[#0F0F0F]">
                <div className="max-w-[1400px] mx-auto px-6 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4 order-2 lg:order-1">
                        {PROVIDER_FEATURES.map((f) => (
                            <div key={f.title} className="border border-gray-200 bg-white p-6 hover:border-gray-400 transition-colors">
                                <HexIcon icon={f.icon} accent={f.accent} size={48} />
                                <div className="font-display text-base font-bold mt-4 mb-2">{f.title}</div>
                                <div className="text-sm text-gray-600 leading-relaxed">{f.desc}</div>
                            </div>
                        ))}
                        <div className="border-2 border-dashed border-gray-300 bg-white p-6 flex flex-col items-start justify-center">
                            <Sparkles className="w-6 h-6 mb-2" style={{ color: C.green }} />
                            <div className="font-display text-base font-bold mb-1">Próximamente</div>
                            <div className="text-sm text-gray-600">Verificación QR del distintivo emitido + sello digital antifalsificación.</div>
                        </div>
                    </div>
                    <div className="lg:col-span-5 lg:sticky lg:top-28 order-1 lg:order-2">
                        <div className="label-xs mb-3" style={{ color: C.orange }}>Para Proveedores</div>
                        <h2 className="font-display text-3xl lg:text-5xl font-black tracking-tight leading-tight mb-6">
                            <span style={{ color: C.teal }}>Acredita</span> tu capacidad y{' '}
                            <span style={{ color: C.orange }}>expande</span> tu presencia.
                        </h2>
                        <p className="text-lg text-gray-700 leading-relaxed mb-6">
                            Destaca tu cumplimiento normativo e intégrate al catálogo líder de proveedores con distintivo.
                            Convierte tu compliance en ventaja competitiva visible.
                        </p>
                        <a
                            href="#contacto"
                            className="inline-flex items-center gap-2 text-white px-6 py-3 font-bold transition-colors"
                            style={{ background: C.orange }}
                            onMouseOver={(e) => (e.currentTarget.style.background = '#c47728')}
                            onMouseOut={(e) => (e.currentTarget.style.background = C.orange)}
                        >
                            Solicitar alta como proveedor <ArrowRight className="w-4 h-4" />
                        </a>
                    </div>
                </div>
            </section>

            {/* Directorio público */}
            <section id="directorio" className="border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-[#0A0A0A]">
                <div className="max-w-[1400px] mx-auto px-6 py-20">
                    <div className="text-center max-w-3xl mx-auto mb-12">
                        <div className="inline-flex items-center gap-2 mb-5 border-2 px-4 py-1.5" style={{ borderColor: '#9D8DF1', background: 'rgba(157,141,241,0.08)' }}>
                            <span className="inline-block w-2 h-2" style={{ background: '#9D8DF1' }}></span>
                            <span className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: '#9D8DF1' }}>Catálogos públicos</span>
                        </div>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.1] mb-5">
                            Directorio de empresas con distintivo
                        </h2>
                        <p className="text-base lg:text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
                            Consulta clientes principales y proveedores con distintivo vigente otorgado por AIGIS.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <DirectorioCard type="client" accent={C.teal} icon={Building2}
                            title="Clientes principales con Distintivo"
                            subtitle="Negocios con distintivo vigente"
                            href="/catalogo/clientes" />
                        <DirectorioCard type="provider" accent={C.orange} icon={Briefcase}
                            title="Proveedores con Distintivo"
                            subtitle="Proveedores con distintivo vigente"
                            href="/catalogo/proveedores" />
                    </div>
                </div>
            </section>

            {/* Misión y Visión */}
            <section id="nosotros" className="border-b border-gray-200 relative">
                <div className="max-w-[1400px] mx-auto px-6 py-20">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                        <div className="border-l-4 pl-8 py-2" style={{ borderColor: C.teal }}>
                            <div className="label-xs mb-3" style={{ color: C.teal }}>◆ Nuestra esencia</div>
                            <h3 className="font-display text-3xl font-black mb-5" style={{ color: C.teal }}>MISIÓN</h3>
                            <p className="text-base leading-relaxed text-gray-700">
                                Empoderar a organizaciones y profesionales mediante una{' '}
                                <span className="font-bold" style={{ color: C.orange }}>plataforma tecnológica de vanguardia</span>{' '}
                                que simplifica y garantiza el cumplimiento de los{' '}
                                <span className="font-bold" style={{ color: C.orange }}>estándares más exigentes</span>.
                                A través de inteligencia artificial y transparencia, transformamos procesos complejos en{' '}
                                <span className="font-bold" style={{ color: C.green }}>certezas operativas</span>,
                                elevando los niveles de seguridad y calidad en cada auditoría.
                            </p>
                        </div>
                        <div className="border-l-4 pl-8 py-2" style={{ borderColor: C.teal }}>
                            <div className="label-xs mb-3" style={{ color: C.teal }}>◆ A dónde vamos</div>
                            <h3 className="font-display text-3xl font-black mb-5" style={{ color: C.teal }}>VISIÓN</h3>
                            <p className="text-base leading-relaxed text-gray-700">
                                Consolidarnos como el{' '}
                                <span className="font-bold" style={{ color: C.orange }}>ecosistema digital líder</span>{' '}
                                en distintivos a nivel global, siendo el referente de confianza e innovación tecnológica
                                que conecte a las industrias con la excelencia, impulsando un mercado más{' '}
                                <span className="font-bold" style={{ color: C.green }}>competitivo, transparente y seguro</span>{' '}
                                para el mundo.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Quiénes Somos */}
            <section className="border-b border-gray-200" style={{ background: C.ink }}>
                <div className="max-w-[1400px] mx-auto px-6 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-4">
                        <img src={LOGO_FULL} alt="AIGIS" className="w-full max-w-[300px] object-contain" />
                    </div>
                    <div className="lg:col-span-8">
                        <div className="label-xs mb-3" style={{ color: C.orange }}>Quiénes Somos</div>
                        <h2 className="font-display text-3xl lg:text-4xl font-black text-white tracking-tight leading-tight mb-6">
                            Un equipo apasionado por la <span style={{ color: C.green }}>excelencia operativa</span>.
                        </h2>
                        <p className="text-base lg:text-lg text-white/85 leading-relaxed">
                            En <span className="font-bold" style={{ color: C.green }}>AIGIS CERTIFICATE</span>,
                            somos un equipo de expertos en tecnología y cumplimiento apasionados por la excelencia operativa.
                            Nacimos con el propósito de eliminar la{' '}
                            <span className="font-bold" style={{ color: C.orange }}>burocracia y la falta de transparencia</span>{' '}
                            en los procesos de auditoría tradicionales. Hemos evolucionado para convertirnos en un{' '}
                            <span className="font-bold" style={{ color: C.orange }}>ecosistema digital robusto</span>{' '}
                            que fusiona la Inteligencia Artificial con los estándares de calidad más altos. No solo digitalizamos
                            procesos; <span className="font-bold" style={{ color: C.green }}>blindamos la operación</span>{' '}
                            de las organizaciones y las proyectamos hacia el éxito global, garantizando que cada validación
                            sea un paso seguro hacia la <span className="font-bold" style={{ color: C.green }}>competitividad internacional</span>.
                        </p>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section id="faq" className="border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-[#0A0A0A]">
                <div className="max-w-[1000px] mx-auto px-6 py-20">
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center gap-2 mb-5 border-2 px-4 py-1.5" style={{ borderColor: '#9D8DF1', background: 'rgba(157,141,241,0.08)' }}>
                            <span className="inline-block w-2 h-2" style={{ background: '#9D8DF1' }}></span>
                            <span className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: '#9D8DF1' }}>Preguntas frecuentes</span>
                        </div>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.1] mb-4">
                            Todo lo que necesitas <span style={{ color: C.green }}>saber</span>
                        </h2>
                        <p className="text-base lg:text-lg text-gray-600 dark:text-gray-400">
                            Resolvemos las dudas más comunes sobre AIGIS CERTIFICATE.
                        </p>
                    </div>
                    <FAQList />
                </div>
            </section>

            <section id="contacto" className="border-b border-gray-200 dark:border-gray-800 relative" style={{ background: '#0A0A0A' }}>
                <div className="max-w-[1400px] mx-auto px-6 py-20">
                    <div className="text-center max-w-3xl mx-auto mb-14">
                        <div className="inline-flex items-center gap-2 mb-5 border-2 px-4 py-1.5" style={{ borderColor: '#9D8DF1', background: 'rgba(157,141,241,0.12)' }}>
                            <span className="inline-block w-2 h-2" style={{ background: '#9D8DF1' }}></span>
                            <span className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: '#9D8DF1' }}>Contacto</span>
                        </div>
                        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] mb-4 text-white">
                            ¿Listo para <span style={{ color: C.green }}>comenzar</span>?
                        </h2>
                        <p className="text-base lg:text-lg text-white/70">
                            Habla con nuestro equipo. Respondemos en menos de 24 horas.
                        </p>
                    </div>
                    <ContactBlock />
                </div>
            </section>

            <section className="bg-white dark:bg-[#0A0A0A]">
                <div className="max-w-[1400px] mx-auto px-6 py-20 text-center">
                    <h2 className="font-display text-3xl lg:text-5xl font-black tracking-tight leading-tight mb-4 max-w-3xl mx-auto">
                        Tu distintivo, <span style={{ color: C.green }}>sin complicaciones</span>.
                    </h2>
                    <p className="text-lg text-gray-700 max-w-2xl mx-auto mb-8">
                        Obtén tu ventaja competitiva. Porque con AIGIS CERTIFICATE demuestras que lo haces mejor que los demás.
                    </p>
                    <div className="flex justify-center gap-3 flex-wrap">
                        <a
                            href="#contacto"
                            className="text-white px-7 py-4 font-bold inline-flex items-center gap-2 transition-colors"
                            style={{ background: C.orange }}
                            onMouseOver={(e) => (e.currentTarget.style.background = '#c47728')}
                            onMouseOut={(e) => (e.currentTarget.style.background = C.orange)}
                        >
                            Solicitar alta <ArrowRight className="w-4 h-4" />
                        </a>
                        <Link
                            to="/login"
                            className="border-2 border-[#0A0A0A] text-[#0A0A0A] px-7 py-4 font-bold hover:bg-gray-50 transition-colors"
                        >
                            Iniciar sesión
                        </Link>
                    </div>
                </div>
            </section>

            <footer className="border-t border-gray-200 bg-white">
                <div className="max-w-[1400px] mx-auto px-6 py-8 flex flex-col md:flex-row items-center justify-between gap-4">
                    <img src={LOGO_WORDMARK} alt="AIGIS CERTIFICATE" className="h-12 w-auto object-contain" />
                    <div className="text-xs text-gray-500">© 2026 AIGIS CERTIFICATE · Safety Solutions. Plataforma global de distintivos de cumplimiento con IA.</div>
                </div>
            </footer>
        </div>
    );
}

function ProfileCard({ icon: Icon, accent, badge, title, body, anchor, roleParam }) {
    return (
        <div className="border border-gray-200 bg-white p-8 hover:border-[#0A0A0A] transition-colors group relative">
            <div className="flex items-start gap-4 mb-4">
                <HexIcon icon={Icon} accent={accent} size={56} />
                <div className="flex-1">
                    <div className="label-xs mb-1" style={{ color: accent }}>{badge}</div>
                    <h3 className="font-display text-2xl font-black tracking-tight">{title}</h3>
                </div>
            </div>
            <p className="text-gray-600 leading-relaxed mb-6">{body}</p>
            <div className="flex flex-wrap gap-2">
                <a
                    href={`#contacto`}
                    className="text-white px-5 py-2.5 text-sm font-bold inline-flex items-center gap-2 transition-colors"
                    style={{ background: C.orange }}
                    onMouseOver={(e) => (e.currentTarget.style.background = '#c47728')}
                    onMouseOut={(e) => (e.currentTarget.style.background = C.orange)}
                >
                    Solicitar alta
                </a>
                <Link
                    to="/login"
                    className="border border-[#0A0A0A] text-[#0A0A0A] px-5 py-2.5 text-sm font-bold hover:bg-gray-50 transition-colors"
                >
                    Iniciar sesión
                </Link>
                <a
                    href={`#${anchor}`}
                    className="px-5 py-2.5 text-sm font-medium text-gray-600 hover:text-[#0A0A0A] transition-colors inline-flex items-center gap-1"
                >
                    Conocer más <ArrowRight className="w-3 h-3" />
                </a>
            </div>
        </div>
    );
}

function DirectorioCard({ type, accent, icon: Icon, title, subtitle, href }) {
    const [items, setItems] = React.useState([]);
    React.useEffect(() => {
        import('@/lib/api').then(({ default: api }) => {
            api.get(`/catalog?type=${type}&limit=3`).then((r) => setItems(r.data));
        });
    }, [type]);

    return (
        <div className="border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#141414] overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-800 flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center shrink-0" style={{ background: accent }}>
                    <Icon className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                    <div className="font-display text-lg font-bold">{title}</div>
                    <div className="text-sm text-gray-500">{subtitle}</div>
                </div>
                <Link to={href} className="text-sm font-bold inline-flex items-center gap-1" style={{ color: accent }}>
                    Ver todo <ArrowRight className="w-3 h-3" />
                </Link>
            </div>
            {items.length === 0 ? (
                <div className="p-8 text-center text-sm text-gray-500">Sin entradas en el catálogo aún.</div>
            ) : (
                <div className="divide-y divide-gray-100 dark:divide-gray-800">
                    {items.slice(0, 3).map((e) => (
                        <Link key={e.id} to={`/catalogo/empresa/${e.id}`} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-sm truncate">{e.name}</div>
                                <div className="text-xs text-gray-500 mt-1 inline-flex items-center gap-2">
                                    <span className="px-2 py-0.5 border border-gray-200 dark:border-gray-700">{e.industry}</span>
                                    <span>◎ {e.location}</span>
                                </div>
                            </div>
                            <span className="text-xs font-bold inline-flex items-center gap-1 shrink-0 ml-3" style={{ color: '#1E9E47' }}>
                                <BadgeCheck className="w-3.5 h-3.5" /> Verificado
                            </span>
                        </Link>
                    ))}
                </div>
            )}
            <Link to={href} className="block px-6 py-4 text-center text-sm font-bold border-t border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-white/5" style={{ color: accent }}>
                Ver catálogo completo →
            </Link>
        </div>
    );
}

const FAQS = [
    { q: '¿Qué son las normas NOM-STPS y por qué son importantes?', a: 'Las Normas Oficiales Mexicanas de la Secretaría del Trabajo (NOM-STPS) son regulaciones obligatorias sobre seguridad e higiene en el trabajo. Su incumplimiento puede resultar en multas, clausura o accidentes laborales graves.' },
    { q: '¿Los distintivos emitidos son legalmente válidos?', a: 'El distintivo AIGIS es un reconocimiento propio de nuestra plataforma, respaldado por evidencia, trazabilidad, firmas digitales y auditoría conforme a los requisitos documentales de la NOM correspondiente. No sustituye acreditaciones oficiales de organismos gubernamentales.' },
    { q: '¿Por qué AIGIS puede cubrir varias normas en una sola auditoría?', a: 'Es nuestra cobertura normativa integral. Una Unidad de Verificación acreditada por la EMA solo puede certificar una norma a la vez, lo que obliga a repetir el proceso —y el costo— por cada NOM que necesites cumplir. AIGIS opera con un enfoque distinto: en una sola visita evaluamos simultáneamente las normas aplicables a tu rubro, generando un único distintivo con cobertura amplia. Menos tiempo, menos fricción, un solo dictamen.' },
    { q: '¿Es gratis registrarse en la plataforma?', a: 'Sí. El registro inicial para establecimientos y proveedores está disponible sin costo durante esta etapa de apertura.' },
    { q: '¿Cómo funciona el asistente IA para catalogar normas?', a: 'Nuestro asistente combina Google Gemini con búsqueda web en tiempo real (Tavily) para ayudar al administrador a dar de alta normas y certificaciones más rápido. Al capturar el código o nombre de una norma, el asistente sugiere código, nombre, descripción y categoría, y adjunta las fuentes verificables donde se apoyó cada dato. El administrador siempre revisa y aprueba antes de guardar. No genera preguntas de checklist; su rol es acelerar la catalogación con respaldo documental.' },
    { q: '¿Funciona la plataforma sin internet?', a: 'La app móvil tiene modo offline completo. El auditor puede llenar el formulario sin conexión, y se sincroniza automáticamente cuando recupera señal. Los datos se marcan con timestamp y GPS para trazabilidad.' },
    { q: '¿Pueden obtener distintivo empresas con múltiples sedes?', a: 'Sí. Podemos acompañarte en esquemas multi-sede y operación extendida, manteniendo trazabilidad, evidencias y control desde un panel central.' },
    { q: '¿Cómo se verifica un distintivo AIGIS?', a: 'Cualquier persona puede escanear el QR del distintivo con su teléfono. Esto abre una página pública en AIGIS donde se muestran todos los datos del distintivo, el alcance normativo, el período de vigencia y el firmante.' },
    { q: '¿Qué pasa si un punto del checklist no se cumple?', a: 'El auditor documenta el hallazgo con evidencia fotográfica y observaciones. La IA analiza la foto y sugiere el marco jurídico. El puntaje final se ajusta automáticamente y el administrador decide aprobar, rechazar o solicitar plan de acción correctivo.' },
    { q: '¿Cómo se renueva un distintivo vencido?', a: 'El distintivo AIGIS tiene una vigencia de 1 año. AIGIS notifica con 30, 15 y 7 días de anticipación a la fecha de vencimiento. La renovación anual reutiliza el historial previo del establecimiento, lo que acelera el nuevo proceso de auditoría en hasta un 60%.' },
];

function FAQList() {
    const [open, setOpen] = useState(null);
    return (
        <div className="space-y-3" data-testid="faq-list">
            {FAQS.map((item, i) => {
                const isOpen = open === i;
                return (
                    <div key={i} className="border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#141414] overflow-hidden">
                        <button
                            onClick={() => setOpen(isOpen ? null : i)}
                            data-testid={`faq-item-${i}`}
                            className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                        >
                            <span className="flex items-start gap-3 flex-1 min-w-0">
                                <span className="inline-flex items-center justify-center w-7 h-7 shrink-0 font-display font-bold text-xs" style={{ background: 'rgba(157,141,241,0.15)', color: '#9D8DF1' }}>
                                    {String(i + 1).padStart(2, '0')}
                                </span>
                                <span className="font-display text-base lg:text-lg font-bold pt-0.5">{item.q}</span>
                            </span>
                            <ChevronDown className={`w-5 h-5 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} style={{ color: '#9D8DF1' }} />
                        </button>
                        {isOpen && (
                            <div className="px-6 pb-5 pl-[68px] text-sm lg:text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                                {item.a}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function ContactBlock() {
    const [form, setForm] = useState({ name: '', email: '', phone: '', role: '', industry: '', certification_interests: [], message: '' });
    const [sending, setSending] = useState(false);
    const [certOptions, setCertOptions] = useState([]);
    const [certsLoading, setCertsLoading] = useState(true);
    const [certOpen, setCertOpen] = useState(false);
    // Iter 46 · rubro dropdown backed by the real catalog (/api/public/rubros)
    // so submissions land as canonical industry names and the onboarding filter
    // matches on the first try (no fallback to "show all").
    const [rubroOptions, setRubroOptions] = useState([]);
    const certDropdownRef = React.useRef(null);
    const set = (k, v) => setForm({ ...form, [k]: v });
    const toggleCert = (v) => {
        setForm((prev) => ({
            ...prev,
            certification_interests: prev.certification_interests.includes(v)
                ? prev.certification_interests.filter((x) => x !== v)
                : [...prev.certification_interests, v],
        }));
    };

    useEffect(() => {
        let cancelled = false;
        api.get('/public/distintivos')
            .then((r) => { if (!cancelled) setCertOptions(r.data || []); })
            .catch(() => { if (!cancelled) setCertOptions([]); })
            .finally(() => { if (!cancelled) setCertsLoading(false); });
        api.get('/public/rubros')
            .then((r) => { if (!cancelled) setRubroOptions(r.data || []); })
            .catch(() => { if (!cancelled) setRubroOptions([]); });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        const onClick = (e) => {
            if (certDropdownRef.current && !certDropdownRef.current.contains(e.target)) setCertOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            // Iter 50 Fase D: also send new distintivo_ids field for future migration.
            const payload = { ...form, distintivo_ids: form.certification_interests };
            await api.post('/contact', payload);
            toast.success('Mensaje enviado. Te responderemos en menos de 24h.');
            setForm({ name: '', email: '', phone: '', role: '', industry: '', certification_interests: [], message: '' });
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Error al enviar. Intenta de nuevo.'));
        } finally { setSending(false); }
    };

    const selectedCerts = certOptions.filter((c) => form.certification_interests.includes(c.id));

    return (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-5 space-y-4">
                <div>
                    <h3 className="font-display text-xl font-bold text-white mb-5">Información de contacto</h3>
                    <div className="space-y-4">
                        <InfoLine icon={Mail} label="Correo" value="contacto@aigiscertificate.com" />
                        <InfoLine icon={Phone} label="Teléfono" value="(+52) 998 104 8874" />
                        <InfoLine icon={MapPin} label="Ubicación" value="Quintana Roo, México" />
                    </div>
                </div>
            </div>
            <form onSubmit={submit} className="lg:col-span-7 border border-gray-800 p-6 lg:p-8" data-testid="contact-form" style={{ background: '#141414' }}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <FormField label="Nombre *" name="contact-name" value={form.name} onChange={(v) => set('name', v)} placeholder="Tu nombre" required testid="contact-name" />
                    <FormField label="Correo electrónico *" type="email" value={form.email} onChange={(v) => set('email', v)} placeholder="tu@correo.com" required testid="contact-email" />
                    <FormField label="Teléfono" value={form.phone} onChange={(v) => set('phone', v)} placeholder="(+52) ..." testid="contact-phone" />
                    <div>
                        <label className="text-xs uppercase tracking-wider text-white/60 font-medium block mb-2">Soy...</label>
                        <select value={form.role} onChange={(e) => set('role', e.target.value)} data-testid="contact-role"
                            className="w-full border border-gray-700 text-white px-4 py-3 focus:border-[#9D8DF1] outline-none" style={{ background: '#0A0A0A' }}>
                            <option value="">Seleccionar...</option>
                            <option value="client">Cliente principal / Establecimiento</option>
                            <option value="provider">Proveedor</option>
                            <option value="other">Otro / Información general</option>
                        </select>
                    </div>
                    <div className="flex flex-col gap-2">
                        <label className="text-xs uppercase tracking-widest font-bold text-gray-500">
                            ¿Cuál es tu giro?
                        </label>
                        <select value={form.industry}
                            onChange={(e) => set('industry', e.target.value)}
                            data-testid="contact-industry"
                            className="w-full px-3 py-2.5 border-2 outline-none text-sm bg-white transition-colors focus:border-black"
                            style={{ borderColor: '#E5E7EB' }}>
                            <option value="">— Selecciona tu giro —</option>
                            {rubroOptions.map((r) => (
                                <option key={r.id} value={r.name}>
                                    {r.name}
                                </option>
                            ))}
                            <option value="Otro">Otro</option>
                        </select>
                    </div>
                    <div ref={certDropdownRef} className="relative">
                        <label className="text-xs uppercase tracking-wider text-white/60 font-medium block mb-2">¿Qué distintivo buscas?</label>
                        <button
                            type="button"
                            onClick={() => setCertOpen((o) => !o)}
                            disabled={certsLoading}
                            data-testid="contact-cert-toggle"
                            className="w-full border border-gray-700 text-white px-4 py-3 focus:border-[#9D8DF1] outline-none text-left flex items-center justify-between text-sm disabled:opacity-60"
                            style={{ background: '#0A0A0A' }}
                        >
                            <span className="truncate">
                                {certsLoading
                                    ? 'Cargando opciones...'
                                    : selectedCerts.length === 0
                                    ? <span className="text-white/40">Ninguna seleccionada</span>
                                    : selectedCerts.length === 1
                                    ? selectedCerts[0].code
                                    : `${selectedCerts.length} seleccionadas`}
                            </span>
                            <ChevronDown className={`w-4 h-4 text-white/50 transition-transform ${certOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {selectedCerts.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                                {selectedCerts.map((c) => (
                                    <span key={c.id} className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5" style={{ background: 'rgba(157,141,241,0.15)', color: '#9D8DF1' }}>
                                        {c.code}
                                        <button type="button" onClick={() => toggleCert(c.id)} data-testid={`contact-cert-remove-${c.id}`} className="hover:opacity-70">
                                            <XIcon className="w-2.5 h-2.5" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                        {certOpen && (
                            <div className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto border border-gray-700 shadow-lg" style={{ background: '#0A0A0A' }} data-testid="contact-cert-dropdown">
                                {certOptions.length === 0 ? (
                                    <div className="p-3 text-xs text-white/50 italic">No hay distintivos disponibles.</div>
                                ) : (
                                    certOptions.map((opt) => {
                                        const isComingSoon = !!opt.coming_soon;
                                        return (
                                        <label key={opt.id}
                                            className={`flex items-start gap-2 px-3 py-2 text-sm text-white border-b border-gray-800 last:border-b-0 ${isComingSoon ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-white/5'}`}
                                            title={isComingSoon ? 'Este distintivo estará disponible próximamente' : ''}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={form.certification_interests.includes(opt.id)}
                                                onChange={() => !isComingSoon && toggleCert(opt.id)}
                                                disabled={isComingSoon}
                                                data-testid={`contact-cert-check-${opt.id}`}
                                                className="accent-[#9D8DF1] mt-1"
                                            />
                                            <div className="min-w-0 flex-1">
                                                <div className="font-bold text-xs flex items-center gap-1.5">
                                                    <span>{opt.code}</span>
                                                    {isComingSoon && (
                                                        <span
                                                            className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5"
                                                            style={{ background: 'rgba(245,158,11,0.2)', color: '#F59E0B' }}
                                                            data-testid={`coming-soon-badge-${opt.code}`}
                                                        >
                                                            Próximamente
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-[11px] text-white/60 truncate">{opt.name}</div>
                                            </div>
                                        </label>
                                        );
                                    })
                                )}
                            </div>
                        )}
                        <div className="text-[10px] text-white/50 mt-1">Puedes seleccionar una o más.</div>
                    </div>
                </div>
                <div className="mb-5">
                    <label className="text-xs uppercase tracking-wider text-white/60 font-medium block mb-2">Mensaje *</label>
                    <textarea required rows={5} value={form.message} onChange={(e) => set('message', e.target.value)} data-testid="contact-message"
                        placeholder="Cuéntanos sobre tu caso..."
                        className="w-full border border-gray-700 text-white px-4 py-3 focus:border-[#9D8DF1] outline-none resize-none" style={{ background: '#0A0A0A' }} />
                </div>
                <button type="submit" disabled={sending} data-testid="contact-submit"
                    className="w-full text-white py-4 font-bold inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                    style={{ background: 'linear-gradient(135deg, #9D8DF1 0%, #6B5BD9 100%)' }}>
                    {sending ? 'Enviando...' : (<><Send className="w-4 h-4" /> Enviar mensaje</>)}
                </button>
            </form>
        </div>
    );
}

function InfoLine({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center shrink-0" style={{ background: 'rgba(157,141,241,0.15)' }}>
                <Icon className="w-5 h-5" style={{ color: '#9D8DF1' }} />
            </div>
            <div>
                <div className="text-xs uppercase tracking-wider text-white/50">{label}</div>
                <div className="text-base font-bold text-white">{value}</div>
            </div>
        </div>
    );
}

function FormField({ label, name, type = 'text', value, onChange, placeholder, required, testid }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-wider text-white/60 font-medium block mb-2">{label}</label>
            <input
                name={name} type={type} value={value} onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder} required={required} data-testid={testid}
                className="w-full border border-gray-700 text-white px-4 py-3 focus:border-[#9D8DF1] outline-none placeholder:text-gray-500"
                style={{ background: '#0A0A0A' }}
            />
        </div>
    );
}
